<template>
    <section>
        <div class="container">
            <CommonBreadcrumb class="bread_block_mod" /> 
        </div>

        <div class="container-fluid px-0">
            <div class="guide_block_banner">
                <div class="eng_guide_banner">
                    <picture>
                        <source width="1920" height="540" type="image/webp"
                            srcset="~/assets/eng-guide/step_three/banner.webp">
                        <img height="540" width="1920" type="image/jpeg" src="~/assets/eng-guide/step_three/banner.jpg"
                            alt="Types of Ring Settings">
                    </picture>
                    <div class="eng_guide_upper">
                        <h2>Step 2</h2>
                        <h3 class="font-active">Choose A Setting</h3>
                        <p class="mb-0" v-if="$country.is_india">When buying your dream ring, it is important to consider
                            the type of engagement ring setting and ring style that looks best with your chosen centre
                            diamond. Be it a solitaire ring style or a ring setting with a diamond-studded halo, the choice
                            of ring setting and ring style will play a significant role in determining your diamond
                            engagement ring's overall aesthetic appeal.</p>
                        <p class="mb-0" v-else>When buying your dream ring, it is important to consider the type of
                            engagement ring setting and ring style that looks best with your chosen center diamond. Be it a
                            solitaire ring style or a ring setting with a diamond studded halo; the choice of ring setting
                            and ring style will play a significant role in determining your diamond engagement ring's
                            overall aesthetic appeal.</p>
                    </div>
                </div>
            </div>
        </div>

        <div class="container-lg mt-5">
            <div class="guide_block pb-4">
                <p v-if="$country.is_india">
                    Ring settings and ring styles refer to the way <NuxtLink :to="localePath('/engagement-rings')">diamond engagement rings</NuxtLink> are styled. It also refers to its aesthetic appeal. At Friendly
                    Diamonds, we offer a wide range of engagement ring settings and ring styles that one can choose from and
                    if you still do not find a type of <NuxtLink :to="localePath({ name: 'cyo-setting-list-ring' })">engagement ring settings</NuxtLink> suitable for you, then you have the option to customise your own
                    diamond engagement ring.
                </p>
                <p v-else>
                    Ring settings and ring styles refer to the way the diamond engagement rings are styled. It also refers
                    to its aesthetic appeal.
                    At Friendly Diamonds, we offer a wide range of <NuxtLink :to="localePath({ name: 'cyo-setting-list-ring' })">engagement ring settings</NuxtLink> and ring
                    styles that one can choose from, and if you still do not find a type of engagement ring setting suitable
                    for you, then you have the option to {{ $t('customize') }} your own diamond engagement ring.
                </p>
            </div>

            <div class="row guide_block pb-4 align-items-center">
                <div class="col-md-5 col-12 text-center">
                    <picture>
                        <source width="500" height="500" type="image/webp"
                            srcset="~/assets/eng-guide/step_three/ring_setting.webp">
                        <img loading="lazy" class=" my-3 radious_eight" height="500" width="500" type="image/jpeg"
                            src="~/assets/eng-guide/step_three/ring_setting.jpg" alt="Choose types of ring settings">
                    </picture>
                </div>
                <div class="col-md-7 col-12">
                    <h1 class="mb-3 font-active">Choose types of ring settings</h1>
                    <p v-if="$country.is_india">
                        There are different types of ring settings that increase the radiance and brilliance of the centre
                        solitaire. The type of ring setting you choose will define your engagement ring's visual aesthetics
                        and style.
                        <br><br>
                        Popular types of ring settings include the prong setting, channel setting, bezel setting, pave
                        setting and cluster setting. These settings are further accentuated with ring styles such as
                        solitaire, halo, side stone and three stone settings.
                    </p>
                    <p v-else>
                        There are different types of ring settings that increase the radiance and brilliance of the center
                        solitaire. The type of ring setting you choose shall define your engagement ring's visual aesthetics
                        and style.
                        <br><br>
                        Popular types of ring settings include the <NuxtLink
                            :to="localePath('/blog/prong-settings-for-diamond-Jewelry')">prong setting</NuxtLink>, channel
                        setting, <NuxtLink :to="localePath('/blog/prong-bezel-settings')">bezel setting</NuxtLink>, pave
                            setting, and cluster setting. These settings are further accentuated with ring styles such as
                            the <NuxtLink
                                :to="localePath({ name: 'cyo-setting-list-ring', params: { start_with: 'solitaire-engagement-rings' } })">solitaire</NuxtLink>,
                            <NuxtLink
                                :to="localePath({ name: 'cyo-setting-list-ring', params: { start_with: 'halo-engagement-rings' } })">halo</NuxtLink>,
                            <NuxtLink :to="localePath({ name: 'cyo-setting-list-ring', params: { start_with: 'side-stone-engagement-rings' } })">sidestone</NuxtLink>, and <NuxtLink
                                :to="localePath({ name: 'cyo-setting-list-ring', params: { start_with: 'three-stone-engagement-rings' } })">three
                                stone settings.</NuxtLink>
                    </p>
                    <div class="eng_guide_flex pt-2">
                        <NuxtLink class="me-2 radious_four" :to="localePath({ name: 'cyo-setting-list-ring' })">Start with Setting
                        </NuxtLink>
                        <NuxtLink class="radious_four" :to="localePath({ name: 'cyo-diamond-list-ring' })">Start with Diamond</NuxtLink>
                    </div>
                </div>
            </div>

            <div class="guide_block py-4">
                <h2 class="mb-3 font-active">Type of Engagement Ring Styles</h2>
                <p v-if="$country.is_india">
                    Discover your favourite type of engagement ring setting and choose from a range of ring styles to create
                    the perfect diamond ring of your dreams.
                </p>
                <p v-else>
                    Discover your favorite type of engagement ring setting and choose from a range of ring styles to create
                    the perfect <NuxtLink :to="localePath({ name: 'cyo-diamond-list-ring' })">diamond ring</NuxtLink> of your
                    dreams.
                </p>
            </div>                    

            <div class="row guide_block py-4 align-items-center">
                <div class="col-md-6 col-12 text-center">
                    <picture>
                        <source width="500" height="500" type="image/webp"
                            srcset="~/assets/eng-guide/step_three/solitaire_ring.webp">
                        <img loading="lazy" class="my-3 radious_eight" height="500" width="500" type="image/jpeg"
                            src="~/assets/eng-guide/step_three/solitaire_ring.jpg" alt="Solitaire rings">
                    </picture>
                </div>
                <div class="col-md-6 col-12">
                    <h4 class="mb-3 font-active">Solitaire rings</h4>
                    <p v-if="$country.is_india">
                        One of the most beautiful and classic styles of all time, the solitaire ring is a type of engagement
                        ring style that features a single brilliant diamond at the centre of the band. This type of
                        engagement ring style is typically accompanied by a prong setting where a number of prongs hold the
                        stone at the centre while it gleams from all sides. The timelessness and simplicity of this setting
                        lend a charming appeal, bringing out the radiance and brilliance of the centre solitaire. To add
                        more glamour to your ring, you can choose from several fancy shaped diamonds and pair them with a
                        diamond-studded matching band.
                    </p>
                    <p v-else>
                        One of the most beautiful and classic styles of all time, the solitaire ring is a type of engagement
                        ring style that features a single brilliant diamond at the center of the band. This type of
                        engagement ring style is typically accompanied by a prong setting where a number of prongs hold the
                        stone at the center while it gleams from all sides. The timelessness and simplicity of this setting
                        lend a charming appeal, bringing out the radiance and brilliance of the center solitaire. To add
                        more glamor to your ring, you can choose from several fancy shaped diamonds and pair it with a
                        diamond studded matching band.
                    </p>
                    <div class="eng_guide_flex pt-2">
                        <NuxtLink class="radious_four"
                            :to="localePath({ name: 'cyo-setting-list-ring', params: { start_with: 'solitaire-engagement-rings' } })">
                            Shop Solitaire Engagement Ring</NuxtLink>
                    </div>
                </div>
            </div>
                        
            <div class="row guide_block py-4 align-items-center row_mod">
                <div class="col-md-6 col-12">
                    <h4 class="mb-3 font-active">Halo rings</h4>
                    <p v-if="$country.is_india">
                        When it comes to ring styles, the halo style has become popular in modern times. Embrace maximum sparkle with this type of engagement ring style that features a stunning centre stone surrounded by a sparkling halo of small diamonds. The Halo is also a type of engagement ring style that adds extra sparkle to any engagement ring and comes in all shapes and sizes. The smaller stones encircling the stone make the centre diamond larger when adorned. Pear cut and oval cut diamonds are the most popular stones used in halo ring styles.
                    </p>
                    <p v-else>
                        When it comes to ring styles, the halo style has become popular in modern times. Embrace maximum sparkle with this type of engagement ring style that features a stunning center stone surrounded by a sparkling halo of small diamonds. The Halo is also a type of engagement ring style that adds extra sparkle to any engagement ring and comes in all shapes and sizes. The smaller stones encircling the stone make the center diamond larger when adorned. <NuxtLink :to="localePath({ name: 'cyo-diamond-list-ring', params: { start_with: 'pear-rings' } })">Pear cut</NuxtLink> and <NuxtLink :to="localePath({ name: 'cyo-diamond-list-ring', params: { start_with: 'oval-rings' } })">oval cut diamonds</NuxtLink> are the most popular stones used in halo ring styles.
                    </p>
                    <div class="eng_guide_flex pt-2">
                        <NuxtLink class="radious_four" :to="localePath({ name: 'cyo-setting-list-ring', params: { start_with: 'halo-engagement-rings' } })">
                            Shop Halo Engagement Ring
                        </NuxtLink>
                    </div>
                </div>
                <div class="col-md-6 col-12 text-center">
                    <picture>
                        <source width="500" height="500" type="image/webp" srcset="~/assets/eng-guide/step_three/halo_ring.webp">
                        <img loading="lazy" class="my-3 radious_eight" height="500" width="500" type="image/jpeg" src="~/assets//eng-guide/step_three/halo_ring.jpg" alt="Halo rings">
                    </picture>
                </div>
            </div>

            <div class="row guide_block py-4 align-items-center">
                <div class="col-md-6 col-12 text-center">
                    <picture>
                        <source width="500" height="500" type="image/webp" srcset="~/assets/eng-guide/step_three/three_stone_ring.webp">
                        <img loading="lazy" class="my-3 radious_eight" height="500" width="500" type="image/jpeg" src="~/assets/eng-guide/step_three/three_stone_ring.jpg" alt="Three stone rings">
                    </picture>
                </div>
                <div class="col-md-6 col-12">
                    <h4 class="mb-3 font-active">Three stone rings</h4>
                    <p v-if="$country.is_india">
                        Popularly known as the trilogy ring, the three stone engagement ring style is the perfect choice for commemorating your special moments and milestones in life. These trilogy stone ring styles are often known to depict a couple's past, present and future. A three stone setting features a centre diamond accompanied by two diamonds at the side, as the name suggests. The side diamonds are usually smaller in size than the centre stone, adding appeal to the ring's overall design. <NuxtLink :to="localePath({ name: 'loose-listing', params: { start_with: 'princess-cut-diamond' } })">Princess</NuxtLink> and <NuxtLink :to="localePath({ name: 'loose-listing', params: { start_with: 'oval-cut-diamond' } })">oval cut diamonds</NuxtLink> are the most common fancy diamonds paired with this type of engagement ring setting.
                    </p>
                    <p v-else>
                        Popularly known as the trilogy rings, the three stone engagement ring style is the perfect choice for commemorating your special moments and milestones in life. These trilogy stone ring styles are often known to depict a couple's past, present & future. A three stone setting features a center diamond accompanied by two diamonds at the side, as the name suggests. The side diamonds are usually smaller in size than the center stone, adding appeal to the ring's overall design. <NuxtLink :to="localePath({ name: 'loose-listing', params: { start_with: 'princess-cut-diamond' } })">Princess</NuxtLink> and <NuxtLink :to="localePath({ name: 'loose-listing', params: { start_with: 'oval-cut-diamond' } })">oval cut diamonds</NuxtLink> are the common fancy diamonds paired with this type of engagement ring setting.
                    </p>
                    <div class="eng_guide_flex pt-2">
                        <NuxtLink class="radious_four" :to="localePath({ name: 'cyo-setting-list-ring', params: { start_with: 'three-stone-engagement-rings' } })">Shop three stone Engagement Ring</NuxtLink>
                    </div>
                </div>
            </div>

            <div class="row guide_block py-4 align-items-center row_mod">
                <div class="col-md-6 col-12">
                    <h4 class="mb-3 font-active">Side stone rings</h4>
                    <p v-if="$country.is_india">
                        When browsing through ring styles and looking for the perfect combination of style and charm, pick a side stone ring style that features smaller diamonds placed elegantly on both sides of the centre diamond. The <NuxtLink :to="localePath({ name: 'loose-listing', params: { start_with: 'round-cut-diamond' } })">brilliant round cut</NuxtLink> and pear cut diamonds are the best centre diamond shapes to go with this type of engagement ring style. They make your ordinary ring more valuable and radiant. Smaller stones are arranged in various ways on either side of the centre stone in this type of engagement ring setting to form a full eternity ring or half eternity ring. These side stones can be set in a channel, micro pavé or shared prong settings. A few ring designs also have bands with diamond embellishments that twist and turn or form double bands. Some side stone rings also feature fancy-shaped diamonds, such as oval or <NuxtLink :to="localePath({ name: 'loose-listing', params: { start_with: 'cushion-cut-diamond' } })">cushion-cut diamonds</NuxtLink>.
                    </p>
                    <p v-else>
                        When browsing through ring styles and looking for the perfect combination of style and charm, pick a side stone ring style that features smaller diamonds placed elegantly on both sides of the center diamond. The brilliant <NuxtLink :to="localePath({ name: 'loose-listing', params: { start_with: 'round-cut-diamond' } })">round cut</NuxtLink> and <NuxtLink :to="localePath({ name: 'loose-listing', params: { start_with: 'pear-cut-diamond' } })">pear cut</NuxtLink> diamonds are the best center <NuxtLink :to="localePath('/blog/types-of-diamond-cut-shape')">diamond shapes</NuxtLink> to go with this type of engagement ring style. They make your ordinary ring more valuable and radiant. Smaller stones are arranged in various ways on either side of the center stone in this type of engagement ring setting to form a full <NuxtLink :to="localePath({ name: 'shop-eternity' })">eternity ring</NuxtLink> or half eternity ring. These side stones can be set in a channel, micro pave, or shared prong settings. A few ring designs also have bands with diamond embellishments that twist and turn or form double bands. Some side stone rings also feature fancy-shaped diamonds, such as oval or <NuxtLink :to="localePath({ name: 'loose-listing', params: { start_with: 'cushion-cut-diamond' } })">cushion-cut diamonds</NuxtLink>.
                    </p>
                    <div class="eng_guide_flex pt-2">
                        <NuxtLink  class="radious_four" :to="localePath({ name: 'cyo-setting-list-ring', params: { start_with: 'side-stone-engagement-rings' } })">Shop Side Stone Engagement Ring</NuxtLink>
                    </div>
                </div>
                <div class="col-md-6 col-12 text-center">
                    <picture>
                        <source width="500" height="500" type="image/webp" srcset="~/assets/eng-guide/step_three/side_stone_ring.webp">
                        <img loading="lazy" class="my-3 radious_eight" height="500" width="500" type="image/jpeg" src="~/assets/eng-guide/step_three/side_stone_ring.jpg" alt="Side stone rings">
                    </picture>
                </div>
            </div>
   
            <div class="row guide_block py-4 align-items-center">
                <div class="col-md-6 col-12 text-center">
                    <picture>
                        <source width="500" height="500" type="image/webp" srcset="~/assets/eng-guide/step_three/vintage_ring.webp">
                        <img loading="lazy" class="my-3 radious_eight" height="500" width="500" type="image/jpeg" src="~/assets/eng-guide/step_three/vintage_ring.jpg" alt="Vintage rings">
                    </picture>
                </div>
                <div class="col-md-6 col-12">
                    <h4 class="mb-3 font-active">Vintage rings</h4>
                    <p v-if="$country.is_india">
                        Vintage engagement ring styles that showcase the beauty of the classic era. This type of engagement ring style features vintage-inspired ring designs with a modern appeal. These types of engagement rings are heirlooms in the making that can be passed down for generations to come. You can pick a cathedral ring setting to hold the diamond in place, giving the design a dreamy appeal, have elaborate carvings on the shank or add a milgrain effect to the design to give it an antique vintage vibe.
                    </p>
                    <p v-else>
                        Vintage engagement ring styles that showcase the beauty of the classic era. This type of engagement ring style features vintage-inspired ring designs with a modern appeal. These types of engagement rings are heirlooms in the making that can be passed down generations to come. You can pick a cathedral ring setting to hold the diamond in place giving the design a dreamy appeal or have elaborate carvings on the shank, or add a milgrain effect to the design to give it an antique vintage vibe.
                    </p>
                    <div class="d-flex pt-2">
                        <NuxtLink class="radious_four" :to="localePath({ name: 'cyo-setting-list-ring', params: { start_with: 'vintage-engagement-rings' } })">Shop Vintage Engagement Ring</NuxtLink>
                    </div>
                </div>
            </div>

            <div class="guide_block py-4">
            <h2 class="mb-3 font-active">Ring Styles Based on Your Personality</h2>
            <p v-if="$country.is_india">
                Buying an engagement ring can be a very tiring and tricky process. And we'll tell you exactly why.
                <br><br>
                <NuxtLink :to="localePath({ name: 'cyo-diamond-list-ring' })">Diamond rings</NuxtLink> are beautiful symbols of your love and will remain with you forever.
                And since they will stay with you until the end, you must invest a lot of time while buying one, for the
                ring will talk about your personality and showcase your style. As different personalities have different
                traits, we have curated a few personality types to help you find the ring of your dreams.
            </p>
            <p v-else>
                Buying an engagement ring can be a very tiring and tricky process. And we'll tell you exactly why.
                <br><br>
                Diamond rings are beautiful symbols of your love and remain with you forever. <br>
                And since they will stay with you until the end, you must invest a lot of time while buying one, for the
                ring will talk about your personality and showcase your style. As different personalities have different
                traits, we have curated a few personality types to help you find the ring of your dreams.
            </p>
            </div>

            <div class="row guide_block py-4 align-items-center">
                <div class="col-md-6 col-12 text-center">
                    <picture>
                        <source width="500" height="500" type="image/webp" srcset="~/assets/eng-guide/step_three/bold.webp">
                        <img loading="lazy" class=" my-3 radious_eight" height="500" width="500" type="image/jpeg"
                            src="~/assets/eng-guide/step_three/bold.jpg" alt="bold & outgoing">
                    </picture>
                </div>
                <div class="col-md-6 col-12">
                    <h4 class="mb-3 font-active" >bold & outgoing</h4>
                    <p v-if="$country.is_india">
                        Is your girl the bold and outgoing type? A halo setting can be the best pick if you want an
                        engagement ring with more glitter to match her persona. A halo of diamonds creates an eye-catching
                        design that makes your centre stone appear larger than it is, making it one of the boldest ring
                        styles ever.
                    </p>
                    <p v-else>
                        Is your girl the bold and outgoing type? A halo setting can be the best pick if you want an
                        engagement ring with more glitter to match her persona. A halo of diamonds creates an eye-catching
                        design that makes your center stone appear larger than it is, making it one of the boldest ring
                        styles ever.
                    </p>
                    <div class="d-flex pt-2">
                        <NuxtLink class="radious_four"
                            :to="localePath({ name: 'cyo-setting-list-ring', params: { start_with: 'halo-engagement-rings' } })">
                            Shop Halo Engagement Ring</NuxtLink>
                    </div>
                </div>
            </div>

            <div class="row guide_block py-4 align-items-center row_mod">
                <div class="col-md-6 col-12">
                    <h4 class="mb-3 font-active">Sophisticated & Worldly</h4>
                    <p v-if="$country.is_india">
                        A vintage-inspired ring is ideal for a partner with a contemporary personality and sophisticated
                        taste. These rings are crafted with engagement ring settings inspired by the Victorian, Edwardian or
                        Art Deco eras. Milgrain and filigree are two exquisite details in vintage settings that give a ring
                        a sophisticated look.
                    </p>
                    <p v-else>
                        A vintage-inspired ring is ideal for a partner with a contemporary personality and sophisticated
                        taste. These rings are crafted with engagement ring settings inspired by Victorian, Edwardian, or
                        Art Deco eras. Milgrain and filigree are two exquisite details in vintage settings that give a ring
                        a sophisticated look.
                    </p>
                    <div class="eng_guide_flex pt-2">
                        <NuxtLink class="radious_four"
                            :to="localePath({ name: 'cyo-setting-list-ring', params: { start_with: 'vintage-engagement-rings' } })">
                            Shop Vintage Engagement Ring</NuxtLink>
                    </div>
                </div>
                <div class="col-md-6 col-12 text-center">
                    <picture>
                        <source width="500" height="500" type="image/webp"
                            srcset="~/assets/eng-guide/step_three/worldly.webp">
                        <img loading="lazy" class="my-3 radious_eight" height="500" width="500"
                            src="~/assets/eng-guide/step_three/worldly.jpg" alt="Sophisticated & Worldly">
                    </picture>
                </div>
            </div>

            <div class="row guide_block py-4 align-items-center">
                <div class="col-md-6 col-12 text-center">
                    <picture>
                        <source width="500" height="500" type="image/webp"
                            srcset="~/assets/eng-guide/step_three/adventure.webp">
                        <img loading="lazy" class="my-3 radious_eight" height="500" width="500"
                            src="~/assets/eng-guide/step_three/adventure.jpg" alt="Adventurous & Outdoorsy">
                    </picture>
                </div>
                <div class="col-md-6 col-12">
                    <h4 class="mb-3 font-active">Adventurous & Outdoorsy</h4>
                    <p v-if="$country.is_india">
                        A solitaire engagement ring is perfect for your partner if she loves the outdoors and enjoys
                        adventure. The simple and sturdy design allows one to be adventurous while still wearing the ring.
                        To add an extra sparkle to her overall personality, your partner can pair the solitaire ring with a
                        variety of diamond eternity wedding band ring styles featuring smaller diamonds.
                    </p>
                    <p v-else>
                        A solitaire engagement ring is perfect for your partner if she loves the outdoors and enjoys
                        adventure. The simple and sturdy design allows one to be adventurous while still wearing the ring.
                        And to add an extra sparkle to her overall personality, your partner can pair the solitaire ring
                        with a variety of diamond eternity wedding band ring styles featuring smaller diamonds.
                    </p>
                    <div class="eng_guide_flex pt-2">
                        <NuxtLink class="radious_four"
                            :to="localePath({ name: 'cyo-setting-list-ring', params: { start_with: 'solitaire-engagement-rings' } })">
                            Shop Solitaire Engagement Rings</NuxtLink>
                    </div>
                </div>
            </div>

            <div class="row guide_block py-4 align-items-center row_mod">
                <div class="col-md-6 col-12">
                    <h4 class="mb-3 font-active">Classic & Traditional</h4>
                    <p v-if="$country.is_india">
                        Side stone engagement rings are perfect if your partner loves a little sparkle but also desires a
                        classic look. This engagement ring setting gives a glamorous appeal to traditional ring styles,
                        whether a set with a few side stones or a full eternity setting.
                    </p>
                    <p v-else>
                        Side stone engagement rings are perfect if your partner loves a little sparkle but also desires a
                        classic look. This engagement ring setting gives a glamorous appeal to traditional ring styles,
                        whether a set with a few side stones or a full eternity setting.
                    </p>
                    <div class="eng_guide_flex pt-2">
                        <NuxtLink class="radious_four"
                            :to="localePath({ name: 'cyo-setting-list-ring', params: { start_with: 'side-stone-engagement-rings' } })">
                            Shop Side Stone Engagement Rings</NuxtLink>
                    </div>
                </div>
                <div class="col-md-6 col-12 text-center">
                    <picture>
                        <source width="500" height="500" type="image/webp"
                            srcset="~/assets/eng-guide/step_three/traditional.webp">
                        <img loading="lazy" class=" my-3 radious_eight" height="500" width="500" type="image/jpeg"
                            src="~/assets/eng-guide/step_three/traditional.jpg" alt="Classic & Traditional">
                    </picture>
                </div>
            </div>

            <div class="guide_block py-4">
                <h2 class="font-active">Pick The Metal Of Your Choice</h2>
            </div>

            <div class="row guide_block pb-4 align-items-center">
                <div class="col-md-12 col-lg-4 col-12">
                    <h4 class="mb-3 font-active">14KT Gold</h4>
                    <p v-if="$country.is_india">14KT gold is the perfect combination of durability and purity, consisting of
                        58% gold. This unique characteristic, in turn, lends a classic and rich appearance that most buyers
                        prefer. It is one of the most commonly used metals for engagement rings, as it is durable and also
                        has high purity, making it an ideal choice.</p>
                    <p v-else>14KT gold is the perfect combination of durability and purity, consisting of 58% gold. This
                        unique characteristic, in turn, lends a classic and rich appearance that most buyers prefer. It is
                        one of the most commonly used metals for engagement rings as they are durable and also have high
                        purity making it the ideal choice.</p>
                </div>
                <div class="col-md-12 col-lg-4 col-12">
                    <h4 class="mb-3 font-active">18KT Gold</h4>
                    <p v-if="$country.is_india">18KT gold is one of the purest golds used to craft diamond jewellery,
                        comprising 75% gold. If we talk about its affordability, it is more expensive than other gold
                        variants as it has a higher gold purity. 18KT gold is most commonly used in making exquisite diamond
                        engagement rings that consist of large diamonds and ones that have more intricate detailing.</p>
                    <p v-else>18KT gold is one of the purest gold used to craft diamond {{ $t('jewelry') }}, comprising of
                        75% gold. If we talk about its affordability, it is more expensive than other gold variants as it
                        has a higher gold purity. 18KT gold is most commonly used in making exquisite diamond engagement
                        rings that consist of large diamonds and ones that have more intricate detailing.</p>
                </div>
                <div class="col-md-12 col-lg-4 col-12">
                    <h4 class="mb-3 font-active">Platinum</h4>
                    <p v-if="$country.is_india">Platinum is one of the most long-lasting metals because it offers a higher
                        range of density. It is 30 times rarer than gold, making it the ideal pick for engagement rings and
                        wedding bands. Platinum is naturally white and there are fewer chances of fading or a change in
                        colour over time. Apart from that, it also offers better protection for your diamond or any other
                        precious stone.</p>
                    <p v-else>Platinum is one of the most long-lasting metals because it offers a higher range of density.
                        It is 30 times rarer than gold, making it the ideal pick for engagement rings and wedding bands.
                        Platinum is naturally white, and there are fewer chances of fading or a change in color over time.
                        Apart from that, it also offers better protection for your diamond or any other precious stone.</p>
                </div>
            </div>

            <div class="guide_block py-4">
                <h2 class="font-active" v-if="$country.is_india">Pick The Metal Colour Of Your Choice</h2>
                <h2 class="font-active" v-else>Pick The Metal Color Of Your Choice</h2>
            </div>

            <div class="row guide_block pb-4">
                <div class="col-md-12 col-lg-4 col-12">
                    <div class="text-center">
                        <NuxtLink
                            :href="localePath({ name: 'cyo-setting-list-ring', params: { start_with: 'white-gold-engagement-rings' } })" class="border-0">
                            <picture>
                                <source width="350" height="350" type="image/webp"
                                    srcset="~/assets/eng-guide/step_three/white_gold.webp">
                                <img loading="lazy" class=" my-3" height="350" width="350" type="image/jpeg"
                                    src="~/assets/eng-guide/step_three/white_gold.jpg" alt="White Gold">
                            </picture>
                        </NuxtLink>
                    </div>

                    <h4 class="mb-3 font-active">White Gold</h4>
                    <p v-if="$country.is_india">
                        Since the last decade, the demand for
                        <NuxtLink
                            :href="localePath({ name: 'cyo-setting-list-ring', params: { start_with: 'white-gold-engagement-rings' } })">
                            white gold engagement rings</NuxtLink>
                        and wedding bands has exploded. It has become the most popular choice, overtaking yellow gold! This
                        glistening metal is a popular option that is perfect for any of our ring designs and is recognized
                        for its elegance. A fashionable choice, white gold engagement rings have a lustrous tint that goes
                        great with cool skin tones.
                    </p>
                    <p v-else>
                        Since the last decade, the demand for white gold engagement rings and wedding bands has exploded. It
                        has become the most popular choice, overtaking yellow gold! This glistening metal is a popular
                        option that is perfect for any of our ring designs and is recognized for its elegance. A fashionable
                        choice, white gold engagement rings have a lustrous tint that goes great with cool skin tones.
                    </p>
                </div>

                <div class="col-md-12 col-lg-4 col-12">
                    <div class="text-center">
                        <NuxtLink
                            :href="localePath({ name: 'cyo-setting-list-ring', params: { start_with: 'yellow-gold-engagement-rings' } })"
                            class="border-0">
                            <picture>
                                <source width="350" height="350" type="image/webp"
                                    srcset="~/assets/eng-guide/step_three/yellow_gold.webp">
                                <img loading="lazy" class=" my-3" height="350" width="350" type="image/jpeg"
                                    src="~/assets/eng-guide/step_three/yellow_gold.jpg" alt="Yellow Gold">
                            </picture>
                        </NuxtLink>
                    </div>

                    <h4 class="mb-3 font-active">Yellow Gold</h4>
                    <p v-if="$country.is_india">
                        <NuxtLink
                            :href="localePath({ name: 'cyo-setting-list-ring', params: { start_with: 'yellow-gold-engagement-rings' } })">Yellow gold</NuxtLink> is a natural color that will never go out of style. For people with warm skin tones, in particular,
                        the rich, vibrant yellow color of the metal is ideal since it enhances the complexion. A beautiful
                        yellow gold background enhances the beauty of white diamonds. If you want a classic, traditional
                        ring, choose yellow gold as your metal.
                    </p>
                    <p v-else>
                        Yellow gold is a natural color that will never go out of style. For people with warm skin tones, in
                        particular, the rich, vibrant yellow color of the metal is ideal since it enhances the complexion. A
                        beautiful yellow gold background enhances the beauty of white diamonds. If you want a classic,
                        traditional ring, choose yellow gold as your metal.
                    </p>
                </div>
                <div class="col-md-12 col-lg-4 col-12">

                    <div class="text-center">

                        <NuxtLink :href="localePath({ name: 'cyo-setting-list-ring', params: { start_with: 'rose-gold-engagement-rings' } })" class="border-0 ">
                            <picture>
                                <source width="350" height="350" type="image/webp" srcset="~/assets/eng-guide/step_three/rose_gold.webp">
                                <img loading="lazy" class=" my-3" height="350" width="350" type="image/jpeg" src="~/assets/eng-guide/step_three/rose_gold.jpg" alt="Rose Gold">
                            </picture>
                        </NuxtLink>
                    </div> 

                    <h4 class="mb-3 font-active">Rose Gold</h4>
                    <p v-if="$country.is_india">
                        Silver, copper, and gold combine to form rose gold. The blush pink hue of rose gold lends a dreamy,
                        romantic touch to your engagement ring. If you have an olive or pink undertone,
                        <NuxtLink :href="localePath({ name: 'cyo-setting-list-ring', params: { start_with: 'rose-gold-engagement-rings' } })">rose gold engagement rings</NuxtLink>
                        are a superb blend of elegance and match well with warm and cold complexions. If you are looking for
                        a unique piece of jewellery, choose rose gold.
                    </p>
                    <p v-else>
                        Silver, copper, and gold combine to form rose gold. The blush pink hue of rose gold lends a dreamy,
                        romantic touch to your engagement ring. If you have an olive or pink undertone, rose gold engagement
                        rings are a superb blend of elegance and match well with warm and cold complexions. If you are
                        looking for a unique piece of {{ $t('jewelry') }}, choose rose gold.
                    </p>
                </div>
            </div>

            <div class="row cta_block">
                <div class="col-md-12 col-12 mx-auto px-0 d-flex align-items-center justify-content-center font-active">
                    <NuxtLink :to="localePath('/engagement-ring-guide/ring-cost')" >
                        Set a Budget
                        <div class="arrow_eng">
                            <SvgHeaderSvg :type="'left'" />
                        </div>
                        Back
                    </NuxtLink>
                    <span>|</span>
                    <NuxtLink :to="localePath('/engagement-ring-guide/types-of-diamonds')">
                        Next
                        <div class="arrow_eng">
                            <SvgHeaderSvg :type="'right'" />
                        </div>
                        Choose A Diamond
                    </NuxtLink>
                </div>
            </div>
        </div>
    </section>
</template>

<script setup>
    const localePath = useLocalePath()
    const { $country } = useNuxtApp()
    useFetchMeta( useNuxtApp(), useRuntimeConfig() );
</script>

<style scoped>

.arrow_eng svg.svg-head {
    height: 13px;
    width: 12px;
    margin: 0 10px;
}

.eng_guide_banner {
    position: relative;
}

.eng_guide_upper {
    position: absolute;
    top: 50%;
    left: 20%;
    width: 25%;
    transform: translate(-50%, -50%);
}

.eng_guide_upper h2 {
    font-size: 16px;
    font-weight: 500;
    text-transform: capitalize;
}

.eng_guide_upper h3 {
    font-size: 32px;
    color: var(--green);
    font-weight: 500;
    text-transform: capitalize;
}

.eng_guide_upper h3 span {
    font-size: 16px;
    font-weight: 500;
    text-transform: capitalize;
    color: #000;
    display: block;
    font-family: Lato;
    margin-bottom: 2%;
}

.eng_guide_upper p {
    font-size: 14px;
    font-weight: 500;
    color: #000;
    line-height: 1.8;
}

.eng_guide_flex {
    display: flex;
    align-items: center;
    justify-content: flex-start;
}

.eng_guide_flex a {
    display: inline-block;
    font-size: 14px;
    padding: 1.5% 5%;
    color: #000;
    text-transform: unset;
    font-weight: 400;
    border: 1px solid #000;
}

/* -------cta */
.row.cta_block a {
    display: flex;
    align-items: center;
    color: #000;
    font-size: 18px;
}

.row.cta_block span {
    font-size: 25px;
    margin: 0 15px;
    font-weight: 300;
}

.row.cta_block {
    margin: 4% 0;
}

/* -------cta */
@media(max-width:1350px) {
    .eng_guide_upper {
        width: 50%;
        left: 30%;
    }
}

@media(max-width:1024px) {
    .eng_guide_upper h3 {
        font-size: 32px;
    }

    .eng_guide_banner img {
        height: 45vh;
        object-fit: cover;
        object-position: 100%;
    }
}

@media(max-width:992px) {

    .guide_block h4,
    .guide_block h1 {
        font-size: 22px !important;
    }

    .eng_guide_banner img {
        display: none;
    }

    .eng_guide_upper {
        width: 100%;
        left: 0;
        transform: unset;
        position: relative;
        top: 0;
    }

    .guide_block_banner {
        padding: 0 36px 5% 36px;
        border-bottom: 1px solid #ccc;
    }

    /* -------cta */
    .row.cta_block a {
        font-size: 16px;
    }

    /* -------cta */
}

@media(max-width:767px) {
    .arrow_eng {
        /* font-size: 40px; */
    }

    .eng_guide_upper h3 {
        font-size: 32px;
    }

    .guide_block_banner {
        padding: 0% 18px 8% 18px;
        border-bottom: 1px solid #ccc;
    }

    .eng_guide_flex {
        flex-wrap: wrap;
    }

    .eng_guide_flex a {
        display: block;
        width: 100%;
        margin: 2% 0 !important;
        text-align: center;
    }

    /* -------cta */
    .row.cta_block a {
        font-size: 14px;
    }

    /* -------cta */
}

/* -------------------------------------------------------------- */

.guide_block h2 {
    font-size: 32px;
    text-transform: capitalize;
    color: var(--green);
}

.guide_block h5 {
    font-size: 16px;
    font-weight: 600;
    color: #6a6161;
}

.guide_block h4,
.guide_block h1 {
    font-size: 26px;
    text-transform: capitalize;
}

h6 {
    text-align: center;
    font-size: 32px;
    padding: 5%;
    color: #6a6161;
}

.guide_block h4 span {
    color: var(--green);
}

.guide_block p {
    font-size: 16px;
    color: #6a6161;
    font-weight: 500;
}

.guide_block p a {
    background: none;
    padding: 0;
    font-size: 16px;
    text-transform: unset;
    color: #6a6161;
    font-weight: 500;
    border: 0;
    text-decoration: underline;
    color: var(--green);
}

.guide_block h3 {
    font-size: 16px;
    font-weight: 600;
    color: var(--green);
}

.guide_block a {
    display: inline-block;
    font-size: 14px;
    padding: 10px 20px;
    color: #000;
    border: 1px solid #000;
    text-transform: uppercase;
    font-weight: 600;
}

.guide_block ul li {
    font-size: 16px;
    color: #6a6161;
    font-weight: 500;
}

@media(max-width: 992px) {
    .guide_block h2 {
        font-size: 26px;
    }

    h6 {
        font-size: 24px;
    }
}

@media(max-width: 767px) {
    .row_mod {
        flex-direction: column-reverse;
    }

    .guide_block h2 {
        font-size: 24px;
    }

    .guide_block h4,
    .guide_block h1 {
        font-size: 24px;
    }

    .guide_block p {
        margin: 3% 0 !important;
    }

    .guide_block h3 {
        font-size: 16px;
    }

    h6 {
        font-size: 18px;
        padding: 5% 0;
    }
}
</style>
